import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  AfterViewInit
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import {
  CalendarEvent,
  CalendarEventAction,
  CalendarEventTimesChangedEvent,
  CalendarView,
} from 'angular-calendar';
import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { RouteService } from 'src/app/core/services/route.service';
import { LoadCalendarData } from 'src/app/core/state/_actions/report.actions';
import { getCalendarData } from 'src/app/core/state/_selectors/report.selector';
import * as moment from 'moment';
import { SubSink } from 'subsink';
import { UpsertEventComponent } from './upsert-event/upsert-event.component';

import { LaunchDarklyService } from 'src/app/core/services/launchdarkly.service';
import { FEATURE_FLAGS } from 'src/app/shared/launchdarkly.constants';
import { LDFlagValue } from 'launchdarkly-js-client-sdk';

const colors: any = {
  red: {
    primary: '#ad2121',
    secondary: '#FAE3E3',
  },
  blue: {
    primary: '#1e90ff',
    secondary: '#D1E8FF',
  },
  yellow: {
    primary: '#e3bc08',
    secondary: '#FDF1BA',
  },
};

@Component({
  selector: 'app-calendar-view',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  host: {
    '[class.accessibility-enabled]': 'enableCalendarAccessibilityValue'
  }
})
export class CalendarComponent implements OnInit, AfterViewInit {
  @Input('fromDashboard') fromDashboard: boolean = false;
  @Input('data') data: any;
  @Input('tableId') _tableId: string | null = null;
  @Input('reportId') _reportId: any | null = null;
  @Input('inputFlexData') inputFlexData: any;
  @Input('isEmbeddedReport') isEmbeddedReport: boolean = false;
  @Input('referenceFieldKey') referenceFieldKey: string = '';
  @Input('lookupFieldValue') lookupFieldValue: any;
  @Output('refreshReportDetails') refreshReportDetails =
    new EventEmitter<any>();
  isUserUnAuthorized: boolean = false;
  events: Array<CalendarEvent> = [];
  subsink = new SubSink();
  view: CalendarView = CalendarView.Month;
  CalendarView = CalendarView;
  viewDate: Date = new Date();
  keyboardInstructions = 'Use Ctrl+Shift + arrow keys to move between dates. Use Tab and Shift+Tab to move forward and backward through all controls, calendar cells, and events. Press Space or Enter to select a cell, open an event, or change views.';
  viewChangeAnnouncement = '';
  modalData!: {
    action: string;
    event: CalendarEvent;
  };
  actions: CalendarEventAction[] = [
    {
      label: '<i class="fas fa-fw fa-pencil-alt"></i>',
      a11yLabel: 'Edit',
      onClick: ({ event }: { event: CalendarEvent }): void => {},
    },
    {
      label: '<i class="fas fa-fw fa-trash-alt"></i>',
      a11yLabel: 'Delete',
      onClick: ({ event }: { event: CalendarEvent }): void => {
        this.events = this.events.filter((iEvent) => iEvent !== event);
        this.events.sort(this.sortDates);
      },
    },
  ];
  refresh: Subject<any> = new Subject();
  activeDayIsOpen: boolean = true;
  reportsData: any;
  private keydownListener?: (event: KeyboardEvent) => void;

  // Feature flag for accessibility
enableCalendarAccessibility$!: Observable<LDFlagValue>;
enableCalendarAccessibilityValue: boolean = false;

  constructor(
    private store: Store,
    private route: RouteService,
    private modal: NgbModal,
    private ldService: LaunchDarklyService // LaunchDarkly service for feature flag management
  ) {}

  ngOnInit(): void {

    // Initialize feature flag observable
  this.enableCalendarAccessibility$ = this.ldService.getFlagValue(
    FEATURE_FLAGS.ENABLE_CALENDAR_ACCESSIBILITY,
    false
  );

  // Subscribe to get synchronous value for logic
  this.subsink.sink = this.enableCalendarAccessibility$.subscribe((value) => {
    this.enableCalendarAccessibilityValue = !!value;
  });

    this.store.dispatch(
      new LoadCalendarData(
        this.tableId,
        this.reportId,
        this.fromDashboard,
        this.isEmbeddedReport,
        this.referenceFieldKey,
        this.lookupFieldValue
      )
    );
    this.loadSubscribers();
    this.setupCalendarCellKeyboardSupport();
  }

  ngAfterViewInit(): void {
    // Only update ARIA labels when feature flag is enabled - enhances screen reader support
    if (this.enableCalendarAccessibilityValue) {
      setTimeout(() => this.updateCalendarAriaLabel(), 100);
    }
  }

  updateCalendarAriaLabel(): void {
    // Feature flag check - only apply accessibility enhancements when enabled
    if (!this.enableCalendarAccessibilityValue) {
      return;
    }

    const monthYear = this.getMonthYearLabel();
    const viewName = this.getViewName();
    const calendarContainers = document.querySelectorAll('.cal-month-view, .cal-week-view, .cal-day-view');

    calendarContainers.forEach(container => {
      const grid = container.querySelector('[role="grid"]');
      if (grid) {
        grid.setAttribute('aria-label', `Grid of calendar cells in ${viewName} view for ${monthYear}. ${this.keyboardInstructions}`);
      }

      container.querySelectorAll('.cal-today').forEach(cell => {
        const label = cell.getAttribute('aria-label');
        if (label && !label.includes('Today')) cell.setAttribute('aria-label', `${label}, Today`);
      });

      container.querySelectorAll('.cal-event, [role="group"]').forEach(el => {
        if (el.textContent?.trim()) el.setAttribute('aria-label', el.textContent.trim());
        el.removeAttribute('role');
      });
    });

    document.querySelectorAll('[role="application"]').forEach(el => {
      el.removeAttribute('role');
    });

    document.querySelectorAll('[role="landmark"]').forEach(el => {
      el.removeAttribute('role');
      el.setAttribute('aria-hidden', 'true');
      el.removeAttribute('tabindex');
    });

    document.querySelectorAll('[role="alert"]').forEach(el => {
      el.removeAttribute('role');
    });

    document.querySelectorAll('.cal-open-day-events').forEach(c => {
      c.removeAttribute('role');
      c.querySelectorAll('span').forEach(el => {
        el.removeAttribute('role');
        el.setAttribute('aria-hidden', 'true');
        el.removeAttribute('tabindex');
      });
      const eventTitles = c.querySelectorAll('mwl-calendar-event-title');
      if (eventTitles.length > 0) {
        c.setAttribute('role', 'row');
        eventTitles.forEach(el => {
          el.setAttribute('role', 'gridcell');
        });
      }
    });
  }

  /**
   * @returns report id to update
   */
  get reportId() {
    return this._reportId ? this._reportId : this.route.paramMap.reportId;
  }

  get tableId() {
    return this._tableId ? this._tableId : this.route.getTableKey();
  }

  getMonthYearLabel(): string {
    return new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' }).format(this.viewDate);
  }

  getViewName(): string {
    if (this.view === CalendarView.Month) return 'month';
    if (this.view === CalendarView.Week) return 'week';
    return 'day';
  }

  getCalendarAriaLabel(): string {
    return `Calendar for ${this.getMonthYearLabel()}. ${this.keyboardInstructions}`;
  }

  loadSubscribers = () => {
    this.subsink.sink = this.store
      .pipe(
        select(getCalendarData),
        filter(
          (data) =>
            !!data[this.tableId] && !!data[this.tableId][this.reportId]
        ),
        map((data) => data[this.tableId][this.reportId])
      )
      .subscribe((data) => {
        var errorMessage: any = data;
        if (errorMessage == 'User is unauthorized.')
          this.isUserUnAuthorized = true;
        else {
          this.reportsData = data.ReportData;
          this.events = this.mapEventsData(data.EventsData);
          this.events.sort(this.sortDates);
          // Update ARIA labels when data loads - only when feature flag is enabled
          if (this.enableCalendarAccessibilityValue) {
            setTimeout(() => this.updateCalendarAriaLabel(), 100);
          }
        }
      });
  };

  dayClicked({ date, events }: { date: Date; events: CalendarEvent[] }): void {
    if (this.isSameMonth(date, this.viewDate)) {
      if (
        (this.isSameDay(this.viewDate, date) && this.activeDayIsOpen === true) ||
        events.length === 0
      ) {
        this.activeDayIsOpen = false;
      } else {
        this.activeDayIsOpen = true;
      }
      this.viewDate = date;
      // Update ARIA labels when day is clicked - only when feature flag is enabled
      if (this.enableCalendarAccessibilityValue) {
        setTimeout(() => this.updateCalendarAriaLabel(), 100);
      }
    }
  }

  hourClicked(event: any): void {
    let modalRef = this.modal.open(UpsertEventComponent, {
      windowClass: 'modal-custom',
      centered: true,
      size: 'lg',
      backdrop: 'static',
      animation: true,
      keyboard: false,
    });
    modalRef.componentInstance.inputEvent = event.event;
    modalRef.componentInstance.tableId = this.tableId;
    modalRef.componentInstance.reportsData = this.reportsData;
    modalRef.result
      .then(() => {})
      .catch(() => {});
  }

  eventTimesChanged({
    event,
    newStart,
    newEnd,
  }: CalendarEventTimesChangedEvent): void {
    this.events = this.events.map((iEvent) => {
      if (iEvent === event) {
        return {
          ...event,
          start: newStart,
          end: newEnd,
        };
      }
      return iEvent;
    });
    this.events.sort(this.sortDates);
  }

  deleteEvent(eventToDelete: CalendarEvent) {
    this.events = this.events.filter((event) => event !== eventToDelete);
    this.events.sort(this.sortDates);
  }

  setView(view: CalendarView) {
    this.view = view;
    // Announce view change to screen readers - only when feature flag is enabled
    if (this.enableCalendarAccessibilityValue) {
      this.viewChangeAnnouncement = `Switched to ${this.getViewName()} view`;
    }
    setTimeout(() => {
      this.viewChangeAnnouncement = '';
      // Update ARIA labels after view change - only when feature flag is enabled
      if (this.enableCalendarAccessibilityValue) {
        this.updateCalendarAriaLabel();
      }
    }, 100);
  }

  closeOpenMonthViewDay() {
    this.activeDayIsOpen = false;
  }

  setupCalendarCellKeyboardSupport() {
    // Only setup keyboard navigation when feature flag is enabled - provides enhanced keyboard accessibility
    if (!this.enableCalendarAccessibilityValue) {
      return;
    }

    if (this.keydownListener) {
      document.removeEventListener('keydown', this.keydownListener);
    }

    this.keydownListener = (e: KeyboardEvent) => {
      if (!(e.target instanceof HTMLElement)) return;
      const t = e.target as HTMLElement;

      if (e.key === ' ') {
        this.handleSpaceKey(e, t);
        return;
      }

      const isArrow = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key);
      const isNav = (!e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey) ||
                    (e.ctrlKey && e.shiftKey && !e.altKey && !e.metaKey);

      if (isArrow && isNav) {
        this.routeArrowKeyNavigation(e, t);
      }
    };
    document.addEventListener('keydown', this.keydownListener);
  }

  private handleSpaceKey(e: KeyboardEvent, t: HTMLElement): void {
    const isMonthCell = t.closest('mwl-calendar-month-view') && (t.classList.contains('cal-cell') || t.classList.contains('cal-day-cell'));
    const isEventTitle = t.tagName.toLowerCase() === 'mwl-calendar-event-title' && t.getAttribute('role') === 'gridcell';
    const isWeekDayEvent = (t.closest('mwl-calendar-week-view') || t.closest('mwl-calendar-day-view')) && t.classList.contains('cal-event');

    if (isMonthCell || isEventTitle || isWeekDayEvent) {
      e.preventDefault();
      t.click();
    }
  }

  private routeArrowKeyNavigation(e: KeyboardEvent, t: HTMLElement): void {
    const inMonth = t.closest('mwl-calendar-month-view');
    const inWeek = t.closest('mwl-calendar-week-view') && !t.closest('mwl-calendar-day-view');
    const inDay = t.closest('mwl-calendar-day-view');

    if (inMonth && (t.classList.contains('cal-cell') || t.classList.contains('cal-day-cell'))) {
      this.handleMonthViewArrowNavigation(e, t);
    } else if ((inWeek || inDay) && t.classList.contains('cal-header') && t.getAttribute('role') === 'columnheader') {
      this.handleWeekDayHeaderNavigation(e, t);
    } else if ((inWeek || inDay) && t.classList.contains('cal-event')) {
      this.handleEventNavigation(e, t);
    }
  }

  handleMonthViewArrowNavigation(e: KeyboardEvent, t: HTMLElement) {
    e.preventDefault();
    const monthView = t.closest('mwl-calendar-month-view');
    if (!monthView) return;

    const cells = Array.from(monthView.querySelectorAll('.cal-cell, .cal-day-cell'));
    const idx = cells.indexOf(t);
    if (idx === -1) return;

    const moveMap: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 7, ArrowUp: -7 };
    let newIdx = idx + (moveMap[e.key] || 0);

    if (e.key === 'ArrowLeft' && idx === 0) newIdx = cells.length - 1;
    else if (e.key === 'ArrowRight') newIdx = newIdx % cells.length;
    else if ((e.key === 'ArrowDown' && newIdx >= cells.length) || (e.key === 'ArrowUp' && idx < 7)) return;

    (cells[newIdx] as HTMLElement)?.focus();
  }

  handleWeekDayHeaderNavigation(e: KeyboardEvent, t: HTMLElement) {
    e.preventDefault();
    const container = t.closest('mwl-calendar-week-view, mwl-calendar-day-view');
    if (!container) return;

    const headers = Array.from(container.querySelectorAll('.cal-header[role="columnheader"]'));
    const idx = headers.indexOf(t);
    if (idx === -1) return;

    const firstEvent = container.querySelector('.cal-event[tabindex]');
    const allEvents = Array.from(container.querySelectorAll('.cal-event[tabindex]'));

    if (e.key === 'ArrowRight') {
      const nextElement = idx === headers.length - 1 ? firstEvent : headers[idx + 1];
      (nextElement as HTMLElement)?.focus();
    } else if (e.key === 'ArrowLeft') {
      const prevElement = idx === 0 ? allEvents[allEvents.length - 1] : headers[idx - 1];
      (prevElement as HTMLElement)?.focus();
    } else if (e.key === 'ArrowDown') {
      (firstEvent as HTMLElement)?.focus();
    }
  }

  handleEventNavigation(e: KeyboardEvent, t: HTMLElement) {
    e.preventDefault();
    const container = t.closest('mwl-calendar-week-view, mwl-calendar-day-view');
    if (!container) return;

    const eventsByCol = Array.from(container.querySelectorAll('.cal-day-column'))
      .map(col => Array.from(col.querySelectorAll('.cal-event[tabindex]')) as HTMLElement[])
      .filter(arr => arr.length > 0);

    let colIdx = -1, eventIdx = -1;
    for (let i = 0; i < eventsByCol.length; i++) {
      const idx = eventsByCol[i].indexOf(t);
      if (idx !== -1) { colIdx = i; eventIdx = idx; break; }
    }
    if (colIdx === -1) return;

    const col = eventsByCol[colIdx];
    const headers = container.querySelectorAll('.cal-header[role="columnheader"]');
    const isDayView = eventsByCol.length === 1;
    const nextCol = (colIdx + 1) % eventsByCol.length;
    const prevCol = (colIdx - 1 + eventsByCol.length) % eventsByCol.length;

    const navigationMap: Record<string, () => void> = {
      ArrowRight: () => {
        if (isDayView) col[(eventIdx + 1) % col.length]?.focus();
        else if (colIdx === eventsByCol.length - 1 && eventIdx === col.length - 1) (headers[0] as HTMLElement)?.focus();
        else eventsByCol[nextCol][Math.min(eventIdx, eventsByCol[nextCol].length - 1)]?.focus();
      },
      ArrowLeft: () => {
        if (isDayView) col[(eventIdx - 1 + col.length) % col.length]?.focus();
        else if (colIdx === 0 && eventIdx === 0) (headers[headers.length - 1] as HTMLElement)?.focus();
        else eventsByCol[prevCol][Math.min(eventIdx, eventsByCol[prevCol].length - 1)]?.focus();
      },
      ArrowDown: () => col[(eventIdx + 1) % col.length]?.focus(),
      ArrowUp: () => {
        if (isDayView) col[(eventIdx - 1 + col.length) % col.length]?.focus();
        else if (eventIdx > 0) col[eventIdx - 1]?.focus();
        else (headers[colIdx] as HTMLElement)?.focus();
      }
    };

    navigationMap[e.key]?.();
  }

  sortDates(a: any, b: any) {
    return a.start - b.start;
  }

  mapEventsData(data: any) {
    return data.map((e: any) => ({
      id: e.Id,
      title: e.EventAllDay
        ? e.EventName + ' - All Day Event'
        : e.EventName +
          ' - ' +
          moment(new Date(e.EventStartDate)).format('hh:mm:ss a') +
            ' - ' +
          moment(new Date(e.EventEndDate)).format('hh:mm:ss a'),
      start: new Date(e.EventStartDate),
      end:
        moment(e.EventEndDate).isValid() && !this.isDateNull(e.EventEndDate)
          ? new Date(e.EventEndDate)
          : e.EventAllDay
          ? new Date(e.EventStartDate)
          : new Date(e.EventEndDate),
      color: {
        primary: e.EventColor ?? '#000000DE',
        secondary: e.EventBackgroundColor ?? '#E9F3FB',
      },
      allDay: e.EventAllDay,
      showEndDate:
        e.EventAllDay &&
        moment(e.EventEndDate).isValid() &&
        !this.isDateNull(e.EventEndDate),
    }));
  }

  isDateNull(date: Date) {
    return moment(date).diff(new Date('0001-01-01T00:00:00')) === 0;
  }

  isSameMonth(date1:Date, date2:Date) {
    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth()
    );
  }

  isSameDay(date1:Date, date2:Date) {
    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
    );
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
    if (this.keydownListener) {
      document.removeEventListener('keydown', this.keydownListener);
    }
  }
}
